import React from "react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useGetSmsEligibilityQuery } from "@/store/rest_api";
import PhoneNumber from "@/model/interface/PhoneNumber";
import { isSmsSendable } from "@/util/a2p";
import { MessageSquareWarning } from "lucide-react";

const REGISTRATION_LINK = "/manage-organisation/telephony?tab=sms-registration";

interface SmsBlockedNoticeProps {
  fromPhoneNumber?: PhoneNumber;
  className?: string;
}

export default function SmsBlockedNotice({ fromPhoneNumber, className }: SmsBlockedNoticeProps) {
  const { data: smsEligibility, isLoading } = useGetSmsEligibilityQuery();

  if (isLoading || !smsEligibility) {
    return null;
  }

  const orgApproved = smsEligibility.orgApproved;

  if (fromPhoneNumber) {
    // A specific number is in play, so judge that one. isSmsSendable already clears OVERRIDE and
    // verified toll-free numbers without needing an approved campaign.
    if (isSmsSendable(fromPhoneNumber, orgApproved)) {
      return null;
    }
  } else if (smsEligibility.allowed) {
    // No number in context. `allowed` is the server's collective answer — it returns true when any
    // of the organisation's numbers can send, including via OVERRIDE — so an approved campaign is
    // not the only way to be sendable, and orgApproved alone would warn when texting works fine.
    return null;
  }

  const message = !orgApproved
    ? smsEligibility.reason || "Texting is disabled until your SMS registration is approved."
    : "This number is not enrolled in your messaging campaign yet, so it cannot send texts.";

  return (
    <Alert className={className}>
      <MessageSquareWarning className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
        <span>{message}</span>
        <Button asChild size="sm" variant="outline">
          <Link to={REGISTRATION_LINK}>
            {orgApproved ? "Manage registration" : "Complete registration"}
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
